import {
  CommonService,
  Exception,
  LaunchDarklyService,
  ToasterService,
} from '@anddone/ui-utilities';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { TableFilterService } from '../../shared/tablefilters/table-filter.service';
import { ReportsService } from '../../../../../services/reports.service';
import { TranslateModule } from '@ngx-translate/core';
import { ReportCard } from '../report-card/report-card';
import { Loader } from '../../../../layout/loader/loader';
import { Report } from '../report.model';
import { ValidationConstant } from '../../../../../constants/validation.constant';
import { AppSetting } from '../../../../../constants/appsetting.constant';

@Component({
  selector: 'app-reports',
  imports: [ReportCard, Loader, TranslateModule],
  templateUrl: './reports.html',
  styleUrl: './reports.scss',
})
export class Reports implements OnInit, OnDestroy {
  reportList: any[] = [];
  isLoader = signal<boolean>(true);

  /*** Local variables needed for table filter system ***/
  searchParamsData: any = {};
  pager: any = {};
  tableActionSub!: Subscription;
  searchInputSub!: Subscription;
  private destroy$ = new Subject<void>();

  reports: Report[] = [];
  featureFlags: any;

  public commonService = inject(CommonService);
  public allyCommonService = inject(AllyCommonService);
  private toasterService = inject(ToasterService);
  private reportsService = inject(ReportsService);
  private tableFilterService = inject(TableFilterService);
  private launchDarklyService = inject(LaunchDarklyService);

  ngOnInit(): void {
    // Get all feature flags for the project and subscribe to changes
    this.featureFlags = this.launchDarklyService.getAllFeatureFlags(
      AppSetting.launchDarklyProjectName
    );
    this.launchDarklyService.LD_emitters[AppSetting.launchDarklyProjectName]?.subscribe(
      (allFlags: any) => {
        this.featureFlags = allFlags;
      }
    );
    this.tableFilterService.resetSearchInput(ValidationConstant.defaultPageSize);
    this.searchInputSub = this.tableFilterService.searchInput
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchInput) => {
        this.searchParamsData = searchInput.searchParams;
        this.pager = searchInput.pager;
        /*** Assign the method that manages your API call here. ***/
        setTimeout(() => {
          this.findReports(this.pager.currentPage);
        }, 1000);
      });

    this.tableFilterService.registerSubscriptions(this.searchInputSub, this.tableActionSub);
    /*** End of Table filter subscription handling ***/
  }

  findReports(pageNumber: number) {
    if (pageNumber <= 0) {
      return;
    }
    if (this.pager.totalPages > 0) {
      if (pageNumber > this.pager.totalPages) {
        return;
      }
    }
    this.searchParamsData = {};
    this.searchParamsData.PageSize = ValidationConstant.defaultPageSize;
    if (this.featureFlags?.enable_report_api_v3) {
      this.searchParamsData.PageIndex = pageNumber;
    } else {
      this.searchParamsData.StartRow = 0;
    }
    this.isLoader.set(true);
    this.reportList = [];
    if (this.featureFlags?.enable_report_api_v3) {
      this.reportsService
        .getAllReports(this.searchParamsData)
        ?.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            const records = res.value?.records || [];
            if (records.length === 0) {
              this.isLoader.set(false);
              this.reportList = [];
            } else {
              const mappedRes = {
                data: records,
                totalRowCount: res.value?.recordCount || 0,
              };
              this.pager = this.commonService.setPager(mappedRes, pageNumber, this.pager);
              records.forEach((ele: any) => {
                ele.subscriptions = [];
                ele.toggle = false;
              });
              this.reportList = records;
              this.getSubscriptionReports();
            }
          },
          error: (e: any) => {
            this.isLoader.set(false);
            const toastMessage = Exception.exceptionMessage(e);
            this.toasterService.showError(toastMessage.join(', '), 'full');
          },
        });
    } else {
      this.reportsService
        .getAllOldReports(this.searchParamsData)
        ?.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            if (res.data?.length == 0) {
              this.isLoader.set(false);
              this.reportList = [];
            } else {
              this.pager = this.commonService.setPager(res, pageNumber, this.pager);
              res.data?.forEach((ele: any) => {
                ele.subscriptions = [];
                ele.toggle = false;
              });
              this.reportList = res.data;
              this.getSubscriptionReports();
            }
          },
          error: (e: any) => {
            this.isLoader.set(false);
            const toastMessage = Exception.exceptionMessage(e);
            this.toasterService.showError(toastMessage.join(', '), 'full');
          },
        });
    }
  }

  /* get subscription reports */
  getSubscriptionReports() {
    if (this.featureFlags?.enable_report_api_v3) {
      this.reportsService
        .getSubscriptionReports()
        ?.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            this.isLoader.set(false);

            const records = res.value?.records || [];

            // Merge subscriptions if any exist
            if (records.length > 0) {
              this.reportList?.forEach((report: any) => {
                const subscription = records.find((r: any) => r.reportId === report.id);

                if (subscription) {
                  report.subscriptions = subscription;
                  report.toggle = subscription.subscriptionStatus === 'On';
                }
              });
            }
            // Always build the reports list
            this.reports = [];

            this.reportList?.forEach((element: any) => {
              const report: any = {
                name: element.reportName,
                subscriptionId: element?.subscriptions?.id,
                reportId: element.id,
                hasAutoGenerate: element.toggle,
              };

              switch (element.reportName) {
                case 'Daily Authorization Report':
                  report.iconImg = './assets/images/AuthReport.svg';
                  break;
                case 'Daily Settlement Report':
                  report.iconImg = './assets/images/SettlementReport.svg';
                  break;
                case 'Daily Refund Report':
                  report.iconImg = './assets/images/RefundReport.svg';
                  break;
                case 'Daily Chargeback Report':
                  report.iconImg = './assets/images/ChargebackReport.svg';
                  break;
                case 'Payment Authorization Report':
                  report.iconImg = './assets/images/PaymentAuthReport.svg';
                  break;
                case 'Daily NACHA Chargebacks Report':
                  report.iconImg = './assets/images/ChargebackReport.svg';
                  break;
                default:
                  report.iconImg = './assets/images/SettlementReport.svg';
              }
              this.reports.push(report);
            });
          },
          error: (err: any) => {
            this.isLoader.set(false);
            const toastMessage = Exception.exceptionMessage(err);
            this.toasterService.showError(toastMessage.join(', '), 'full');
          },
        });
    } else {
      this.reportsService
        .getOldSubscriptionReports()
        ?.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            this.isLoader.set(false);

            // Merge subscriptions if any exist
            if (res.data.length > 0) {
              this.reportList?.forEach((ele1: any) => {
                res.data.forEach((ele2: any) => {
                  if (ele1.id == ele2.reportId) {
                    ele1.subscriptions = ele2;
                    ele1.toggle = ele2.subscriptionStatus === 'On';
                  }
                });
              });
            }

            // Always build reports
            this.reports = [];
            this.reportList?.forEach((element: any) => {
              const report: any = {};

              report.name = element.reportName;
              report.subscriptionId = element?.subscriptions?.id;
              report.reportId = element.id;
              report.hasAutoGenerate = element.toggle;
              switch (element.reportName) {
                case 'Daily Authorization Report':
                  report.iconImg = './assets/images/AuthReport.svg';
                  break;
                case 'Daily Settlement Report':
                  report.iconImg = './assets/images/SettlementReport.svg';
                  break;
                case 'Daily Refund Report':
                  report.iconImg = './assets/images/RefundReport.svg';
                  break;
                case 'Daily Chargeback Report':
                  report.iconImg = './assets/images/ChargebackReport.svg';
                  break;
                case 'Payment Authorization Report':
                  report.iconImg = './assets/images/PaymentAuthReport.svg';
                  break;
                case 'Daily NACHA Chargebacks Report':
                  report.iconImg = './assets/images/ChargebackReport.svg';
                  break;
                default:
                  report.iconImg = './assets/images/SettlementReport.svg';
              }
              this.reports.push(report);
            });
          },
          error: (err: any) => {
            this.isLoader.set(false);
            const toastMessage = Exception.exceptionMessage(err);
            this.toasterService.showError(toastMessage.join(', '), 'full');
          },
        });
    }
  }

  receiveData(event: string) {
    if (event === 'refreshReports') {
      this.findReports(1);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(); // Triggers takeUntil
    this.destroy$.complete(); // Final cleanup
  }
}
